import { App } from 'app/core/app/app.model';
import { getProductDisplay } from 'app/core/payment/product-display';

/**
 * The console's products, in the order it lists them everywhere else. Only
 * these count: Payment also reports add-ons as activated products (Amanda is
 * a Cortex plugin), which are not products of their own.
 */
export const PRODUCT_ORDER: readonly string[] = [
  'voice',
  'rooms',
  'fleet',
  'cortex',
];

/** An activated product as the project list and switcher show it. */
export interface ProjectProduct {
  slug: string;
  /** Short name: `Voice`, not `ODIN Voice`. */
  name: string;
  /** The product's own mark, as in the sidebar. */
  icon: string;
}

/** A filter tab: every project, or those with one product activated. */
export interface ProductFilter {
  value: string;
  label: string;
  count: number;
}

/** The project's activated products, in {@link PRODUCT_ORDER}. */
export function projectProducts(app: App): ProjectProduct[] {
  const slugs = new Set(
    (app.activatedProducts ?? [])
      .map((entry) => entry.product?.slug)
      .filter((slug): slug is string => !!slug && PRODUCT_ORDER.includes(slug)),
  );
  return [...slugs]
    .sort((a, b) => PRODUCT_ORDER.indexOf(a) - PRODUCT_ORDER.indexOf(b))
    .map((slug) => ({
      slug,
      name: getProductDisplay(slug).name.replace(/^ODIN\s+/, ''),
      icon: `odin-${slug}`,
    }));
}

/** Whether a project passes a filter tab (`all` passes everything). */
export function matchesProductFilter(app: App, filter: string): boolean {
  return (
    filter === 'all' ||
    !!app.activatedProducts?.some((entry) => entry.product?.slug === filter)
  );
}

/**
 * The filter tabs for a project list: `All` plus one per activated product,
 * counted over `matches` (the projects the current search leaves).
 *
 * Empty when no tab would change the list — every project has the same
 * products, as on Payment-free builds where each one is a Fleet app — so the
 * row does not show choices that all mean "everything".
 */
export function productFilters(
  apps: readonly App[],
  matches: readonly App[],
): ProductFilter[] {
  const slugs = new Map<string, string>();
  for (const app of apps) {
    for (const product of projectProducts(app)) {
      slugs.set(product.slug, product.name);
    }
  }
  const useful = [...slugs.keys()].some(
    (slug) =>
      apps.filter((app) => matchesProductFilter(app, slug)).length <
      apps.length,
  );
  if (!useful) return [];

  return [
    { value: 'all', label: 'All', count: matches.length },
    ...[...slugs.entries()]
      .sort(([a], [b]) => PRODUCT_ORDER.indexOf(a) - PRODUCT_ORDER.indexOf(b))
      .map(([slug, label]) => ({
        value: slug,
        label,
        count: matches.filter((app) => matchesProductFilter(app, slug)).length,
      })),
  ];
}
